import React, { useState, useEffect } from "react";
import '../ut_styles/randomColorz.scss';  
import SvgIcn from "../data/IconCompo";
import { updateFavicon } from "../utils/favicon";
import BackButton from '../components/BackButton';
import { ReactComponent as GuideIcon } from "../assets/icons/guide.svg";

const RandomColorz = () => {
  useEffect(() => {
    updateFavicon("random");
  }, []);

  const [colors, setColors] = useState([
    { hex: generateRandomColor(), locked: false },
  ]);
  const [colorCount, setColorCount] = useState(1);
  const [copiedColor, setCopiedColor] = useState(null);
  const [colorHistory, setColorHistory] = useState([]);
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === "Space" || event.code === "ArrowUp") {
        event.preventDefault();
        generateColors();
      } else if (event.code === "ArrowRight") {
        event.preventDefault();
        addColor();
      } else if (event.code === "ArrowLeft") {
        event.preventDefault();
        removeColor();
      } else if (event.code === "ArrowDown") {
        event.preventDefault();
        bringBackLastSet();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [colorCount, colorHistory]);

  function generateRandomColor() {
    const hex = Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, "0");
    return `#${hex}`;
  }

  function generateColors() {
    setColorHistory((prev) => [colors, ...prev.slice(0, 1)]);
    setColors((prevColors) =>
      prevColors.map((color) =>
        color.locked ? color : { ...color, hex: generateRandomColor() }
      )
    );
  }

  function addColor() {
    if (colorCount < 7) {
      setColorCount((prevCount) => prevCount + 1);
      setColors((prevColors) => [
        ...prevColors,
        { hex: generateRandomColor(), locked: false },
      ]);
    }
  }

  function removeColor() {
    if (colorCount > 1) {
      setColors((prevColors) => {
        const newColors = [...prevColors];
        for (let i = newColors.length - 1; i >= 0; i--) {
          if (!newColors[i].locked) {
            newColors.splice(i, 1);
            setColorCount((prevCount) => prevCount - 1);
            return newColors;
          }
        }
        return prevColors; // If all colors are locked, don't remove any
      });
    }
  }

  function bringBackLastSet() {
    if (colorHistory.length > 0) {
      const lastSet = colorHistory[0];
      const currentLocked = colors.filter((color) => color.locked);
      const newColors = lastSet.map((color) => ({ ...color, locked: false }));

      // Merge locked colors from current set
      currentLocked.forEach((lockedColor) => {
        const index = newColors.findIndex((color) => !color.locked);
        if (index !== -1) {
          newColors[index] = lockedColor;
        } else {
          newColors.push(lockedColor);
        }
      });

      // Remove any excess white space
      while (
        newColors.length > 1 &&
        newColors[newColors.length - 1].hex === "#FFFFFF"
      ) {
        newColors.pop();
      }

      setColors(newColors);
      setColorCount(newColors.length);
      setColorHistory((prev) => prev.slice(1));
    }
  }

  function toggleLock(index) {
    setColors((prevColors) =>
      prevColors.map((color, i) =>
        i === index ? { ...color, locked: !color.locked } : color
      )
    );
  }

  function copyToClipboard(hex) {
    navigator.clipboard.writeText(hex).then(() => {
      setCopiedColor(hex);
      setTimeout(() => setCopiedColor(null), 2000);
    });
  }

  return (
    <div className="random-colorz">
      <BackButton />
      {copiedColor && <div className="tooltip">Color HEX Copied!</div>}
      {isGuideOpen && (
        <div
          id="rcz-guide-panel"
          className="rcz-guide"
          role="note"
          aria-label="Random Colorz guide"
        >
          {/* <div className="rcz-guide__title">Random Colorz</div>
          <div className="rcz-guide__text">
            Generate fast color palettes, pin the swatches you like, and copy HEX
            values for design/dev.
          </div> */}
          {/* <div className="rcz-guide__shortcuts">
            <span className="rcz-guide__pill">Space / ↑ Shuffle</span>
            <span className="rcz-guide__pill">→ Add swatch</span>
            <span className="rcz-guide__pill">← Remove swatch</span>
            <span className="rcz-guide__pill">↓ Undo</span>
            <span className="rcz-guide__pill">Pin = lock color</span>
            <span className="rcz-guide__pill">COPY = copy HEX</span>
          </div> */}
          <SvgIcn name="guide" className="guide-icon" />
        </div>
      )}
      <div className="color-container">
        {colors.map((color, index) => (
          <div
            key={index}
            className="color-box"
            style={{
              backgroundColor: color.hex,
              width: `${100 / colorCount}%`,
            }}
          >
            <p>{color.hex}</p>
            <button
              onClick={() => copyToClipboard(color.hex)}
              className="copy-button"
            >
              COPY
            </button>
            <button
              onClick={() => toggleLock(index)}
              className={`lock-button ${color.locked ? "locked" : ""}`}
            >
              {color.locked ? <SvgIcn name="unpin" /> : <SvgIcn name="pin" />}
            </button>
          </div>
        ))}
      </div>
      <div className="controls">
        <button
          onClick={removeColor}
          disabled={colorCount <= 1}
          className="icon-button"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 12H19"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          onClick={addColor}
          disabled={colorCount >= 7}
          className="icon-button"
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 5V19M5 12H19"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>
      <button
        type="button"
        className={`rcz-guide-btn ${isGuideOpen ? "open" : ""}`}
        onClick={() => setIsGuideOpen((v) => !v)}
        aria-expanded={isGuideOpen}
        aria-controls="rcz-guide-panel"
        title="Guide"
      >
        <SvgIcn name="hand2" />
      </button>
    </div>
  );
};

export default RandomColorz;
